import { useSEO } from "../components/SEO";
import { PageHero, Reveal, SectionTag } from "../components/Reveal";
import { CtaBand } from "../components/CtaBand";

const FOCUS = [
  "Specialization",
  "Customer acquisition",
  "Performance",
  "Communication",
  "Strategy",
  "Long-term growth",
];

export default function About() {
  useSEO(
    "About Nexus Catalyst Agency | Hajj & Umrah Marketing Specialists",
    "Nexus Catalyst was created around one idea: Hajj & Umrah travel agencies in India deserve specialized marketing, not generic strategies."
  );

  return (
    <main data-testid="about-page">
      <PageHero
        tag="About Nexus Catalyst"
        testid="about-hero"
        crumbs={[{ label: "About", to: "/about" }]}
        lines={[
          "Built for an industry that",
          <>deserves <span className="text-tan">specialized marketing.</span></>,
        ]}
        copy="Nexus Catalyst was created around a simple idea: Hajj & Umrah travel agencies should not have to rely on generic marketing strategies."
      />

      <section className="bg-white py-24 md:py-32" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionTag className="mb-6">Why We Exist</SectionTag>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-charcoal leading-[1.1]">
                Generalist agencies sell the same playbook to every industry.
                We chose one.
              </h2>
              <div className="mt-8 space-y-5 text-base text-charcoal/65 leading-relaxed max-w-2xl">
                <p>
                  Hajj &amp; Umrah travel is unlike any other market. The
                  customer journey runs through WhatsApp conversations, family
                  discussions, package comparisons and deep trust, across
                  India's metros and regional markets alike.
                </p>
                <p>
                  Nexus Catalyst exists to serve that reality: a growth
                  partner that understands how digital enquiries become real
                  travel bookings for Indian pilgrimage travel businesses,
                  from Mumbai and Surat to Hyderabad, Lucknow, Kolkata and
                  Kerala.
                </p>
                <p>
                  We are not a travel agency, and we don't sell packages. We
                  build the marketing systems that bring qualified customer
                  conversations to the agencies that do.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="bg-cream border border-charcoal/10 p-8">
                <div className="text-xs font-semibold tracking-[0.25em] uppercase text-tan mb-5">
                  What the Agency Focuses On
                </div>
                <div className="space-y-3">
                  {FOCUS.map((f, i) => (
                    <div
                      key={f}
                      className="flex items-center gap-4 border-b border-charcoal/10 pb-3 last:border-0"
                      data-testid={`about-focus-${i + 1}`}
                    >
                      <span className="text-xs font-semibold tracking-[0.25em] text-gold">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm font-semibold text-charcoal">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-cream py-24 md:py-32" data-testid="founder-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-4">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-gold/50" />
              <div className="relative aspect-[4/5] bg-cream-dark geo-pattern flex items-center justify-center border border-charcoal/10">
                <div className="text-center px-6">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center border border-gold/50 bg-cream">
                    <span className="text-lg font-bold text-tan tracking-tight">NC</span>
                  </div>
                  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-charcoal/50" data-testid="founder-image-placeholder">
                    Founder photograph: replace with your image
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <SectionTag className="mb-6">The Founder</SectionTag>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-charcoal leading-[1.1]">
                Started with a frustration, built with a focus.
              </h2>
              <div className="mt-6 space-y-5 text-base text-charcoal/65 leading-relaxed max-w-2xl">
                <p>
                  Nexus Catalyst was founded on an observation that kept
                  repeating: capable Hajj &amp; Umrah travel agencies were
                  being served generic marketing by agencies that didn't
                  understand the customer, the season, or the stakes of the
                  journey.
                </p>
                <p>
                  This agency is the answer to that: one market, studied
                  deeply, served properly.
                </p>
              </div>
              <p className="mt-6 text-xs text-charcoal/45" data-testid="founder-editable-note">
                Editable founder section: add your name, story and background
                here when ready.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand testid="about-cta-band" />
    </main>
  );
}
